import { EchartsWithTheme, Empty, useTheme, Key, Radio, Themes } from '@maxtropy/components';
import { AllEPriceTime, EPriceTime, getAllDates, getEPriceTimesByDay, DateType, formatNumber } from '../utils';
import { EchartsWithThemeRef } from '@maxtropy/components/es/components/EchartsWithTheme';
import { getChartOption, totalOption } from './components/chartOption';
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { isNil, isEmpty } from 'lodash-es';
import tittleDecoration from '../tittleDecoration.png';
import { RadioChangeEvent, Spin } from 'antd';
import styles from './index.module.scss';
import { EnergyMonitorContext } from '../';
import EnergyStats from './EnergyCard';
import ResizeObserver from 'rc-resize-observer';
import dayjs from 'dayjs';
import {
  apiV2HighEnergyDeviceV2EnergyMonitoringPost,
  V2HighEnergyDeviceV2EnergyMonitoringPostResponse,
  apiV2EnergyUnitBoardDetailEnergyMediumInEnergyUnitPost,
  apiV2HighEnergyDeviceElectricityPricePost,
  apiV2HighEnergyDeviceV2GetStateEnergyDataPost,
  V2HighEnergyDeviceV2GetStateEnergyDataPostResponse,
} from '@maxtropy/device-customer-apis-v2';
import useLatestRequest from '../useLatestRequest';

interface MakeUseOfProps {
  dataQueryPeriodId?: number;
  timeResolution?: string;
  displayValue?: any;
  timeRangeTidy: number[];
  stateOnChange: (data: StateEnergyProps) => void;
  onSendMediumId: (str?: string) => void;
}
interface TabItemProps {
  name: string;
  id: string;
  sceneId: Key;
}

export type DataProps = Exclude<V2HighEnergyDeviceV2EnergyMonitoringPostResponse, undefined>;
export type StateEnergyProps = Exclude<V2HighEnergyDeviceV2GetStateEnergyDataPostResponse, undefined>;

const ELECTRICID = '2030';
const MakeUseOf: React.FC<MakeUseOfProps> = ({
  timeRangeTidy,
  timeResolution,
  dataQueryPeriodId,
  stateOnChange,
  onSendMediumId,
}) => {
  const { unitId, deviceLedgerId } = React.useContext(EnergyMonitorContext);
  const [energyMediumId, setEnergyMediumId] = useState<string>();
  const [lifeEnergy, setLifeEnergy] = useState<TabItemProps[]>([]);
  const [datas, setDatas] = useState<DataProps>({});
  const [energyLoading, setEnergyLoading] = useState<boolean>(false);
  const [stateEnergyLoading, setStateEnergyLoading] = useState<boolean>(false);
  const [ePriceTimes, setEPriceTimes] = useState<EPriceTime[]>();
  const [stateEnergyData, setStateEnergyData] = useState<StateEnergyProps>({});
  const theme = useTheme();
  const chartRef = useRef<EchartsWithThemeRef>();
  const { runLatest: runLatestMedium, cancelLatest: cancelLatestMedium } = useLatestRequest();
  const { runLatest: runLatestEnergy, cancelLatest: cancelLatestEnergy } = useLatestRequest();
  const { runLatest: runLatestStateEnergy, cancelLatest: cancelLatestStateEnergy } = useLatestRequest();
  const { runLatest: runLatestPrice, cancelLatest: cancelLatestPrice } = useLatestRequest();
  const loading = energyLoading || stateEnergyLoading;

  useEffect(() => {
    setLifeEnergy([]);
    setEnergyMediumId(undefined);
    onSendMediumId?.(undefined);
    if (!unitId) {
      cancelLatestMedium();
      return;
    }
    runLatestMedium(() => apiV2EnergyUnitBoardDetailEnergyMediumInEnergyUnitPost({ energyUnitId: unitId }), {
      onSuccess: res => {
        const items = (res.list ?? []).map(item => ({
          name: item.energyName,
          id: String(item.id),
          sceneId: item.sceneId,
        }));
        setLifeEnergy(items as TabItemProps[]);
        setEnergyMediumId(items?.[0]?.id);
        onSendMediumId?.(items?.[0]?.id);
      },
    });
  }, [cancelLatestMedium, onSendMediumId, runLatestMedium, unitId]);

  useEffect(() => {
    const hasAllParams = Boolean(unitId && energyMediumId && timeRangeTidy);
    if (!hasAllParams || !deviceLedgerId) {
      cancelLatestEnergy();
      cancelLatestStateEnergy();
      setDatas({});
      setStateEnergyData({});
      stateOnChange({});
      setEnergyLoading(false);
      setStateEnergyLoading(false);
      return;
    }

    setEnergyLoading(true);
    runLatestEnergy(
      () =>
        apiV2HighEnergyDeviceV2EnergyMonitoringPost({
          energyUnitId: unitId,
          timeResolution,
          energyMediumId,
          startTime: timeRangeTidy[0],
          endTime: timeRangeTidy[1],
          dataQueryPeriodId,
          deviceLedgerId,
        }),
      {
        onSuccess: setDatas,
        onSettled: () => setEnergyLoading(false),
      }
    );

    setStateEnergyLoading(true);
    runLatestStateEnergy(
      () =>
        apiV2HighEnergyDeviceV2GetStateEnergyDataPost({
          energyUnitId: unitId,
          timeResolution,
          energyMediumId,
          startTime: timeRangeTidy[0],
          endTime: timeRangeTidy[1],
          dataQueryPeriodId,
          inventoryId: deviceLedgerId,
        }),
      {
        onSuccess: res => {
          setStateEnergyData(res);
          stateOnChange(res);
        },
        onSettled: () => setStateEnergyLoading(false),
      }
    );
  }, [
    cancelLatestEnergy,
    cancelLatestStateEnergy,
    dataQueryPeriodId,
    deviceLedgerId,
    energyMediumId,
    runLatestEnergy,
    runLatestStateEnergy,
    stateOnChange,
    timeRangeTidy,
    timeResolution,
    unitId,
  ]);

  useEffect(() => {
    if (!unitId || !timeRangeTidy) {
      cancelLatestPrice();
      setEPriceTimes(undefined);
      return;
    }
    runLatestPrice(
      () =>
        apiV2HighEnergyDeviceElectricityPricePost({
          energyUnitId: unitId,
          startTime: timeRangeTidy[0],
          endTime: timeRangeTidy[1],
        }),
      {
        onSuccess: res => {
          const dates = getAllDates(timeRangeTidy[0], timeRangeTidy[1]);
          setEPriceTimes(getEPriceTimesByDay(dates, (res.priceIntervals ?? {}) as unknown as AllEPriceTime));
        },
      }
    );
  }, [cancelLatestPrice, runLatestPrice, timeRangeTidy, unitId]);

  const onRadioChange = (e: RadioChangeEvent) => {
    const value = e.target.value;
    setEnergyMediumId(value);
    onSendMediumId(value);
  };

  const xLabel = useMemo(() => {
    if (timeResolution === DateType.MONTH || timeResolution === DateType.WEEK) {
      return 'YYYY-MM-DD';
    } else if (timeResolution === DateType.YEAR) {
      return 'YYYY-MM';
    } else {
      return 'HH:mm';
    }
  }, [timeResolution]);

  const _option = useMemo(() => {
    if (!energyMediumId) {
      return {};
    }

    switch (energyMediumId) {
      case ELECTRICID:
        return getChartOption(datas, stateEnergyData, timeResolution!, ePriceTimes!, theme);
      default: {
        const energyMediumName = lifeEnergy.find(i => i.id === energyMediumId)?.name;
        return energyMediumName ? totalOption(datas, stateEnergyData, timeResolution!, energyMediumName, theme) : {};
      }
    }
  }, [datas, energyMediumId, stateEnergyData, ePriceTimes, lifeEnergy, theme, timeResolution]);

  return (
    <div className={styles.container}>
      <div className={styles.headerWrapper}>
        <div>
          <img src={tittleDecoration} alt="pic" className={styles.titleBorder} />
          <span className={styles.title}>用能监测</span>
        </div>
        <div style={{ paddingRight: 12 }}>
          <Radio.Group onChange={onRadioChange} value={energyMediumId} buttonStyle="solid">
            {lifeEnergy?.map(i => (
              <Radio.Button key={i.id} value={i.id}>
                {i.name}
              </Radio.Button>
            ))}
          </Radio.Group>
        </div>
      </div>
      <div className={styles.betweenCard}>
        <EnergyStats datas={datas} energyMediumId={energyMediumId} timeResolution={timeResolution} />
      </div>
      <div className={styles.content_box}>
        <div className={styles.chart_box}>
          <ResizeObserver>
            <Spin spinning={loading}>
              <EchartsWithTheme
                style={{ height: 480 }}
                theme={theme}
                empty={{
                  isShowEmpty: isEmpty(datas),
                  emptyNode: <Empty style={{ height: 480, padding: '120px 80px' }} />,
                }}
                option={_option}
                ref={d => {
                  if (d) {
                    chartRef.current = d;
                  }
                }}
                onEvents={{
                  brushSelected: (params: any) => {
                    let startTime;
                    let endTime;
                    let totalUsage = 0; // 总用量
                    let totalCost = 0; // 总成本
                    const brushComponent = params.batch[0];
                    const chartObject = chartRef.current?.getInstance();
                    const option = chartObject?.getOption();
                    const series = option?.series as any[];

                    const rich: any = {};
                    const lines: string[] = [];

                    const getPointValue = (point: any) => {
                      if (Array.isArray(point)) {
                        return point[1];
                      }
                      if (Array.isArray(point?.value)) {
                        return point.value[1];
                      }
                      return point?.value;
                    };

                    const getPointMeta = (point: any) => {
                      if (Array.isArray(point)) {
                        return point[2];
                      }
                      if (Array.isArray(point?.value)) {
                        return point.value[2];
                      }
                      return point?.meta;
                    };

                    brushComponent.selected.forEach((item: any) => {
                      const findSeries = series?.find(i => i.name === item.seriesName);
                      if (!findSeries || !Array.isArray(findSeries.data) || !item?.dataIndex?.length) {
                        return;
                      }

                      let arr: any[] = [];
                      item.dataIndex.forEach((c: number, index: number) => {
                        const point = findSeries.data?.[c];
                        if (!point) {
                          return;
                        }

                        const pointValue = getPointValue(point);
                        const pointMeta = getPointMeta(point);

                        if (index === 0) {
                          startTime = point[0];
                        }
                        if (index === item.dataIndex.length - 1) {
                          endTime = point[0];
                        }
                        arr.push(pointValue);

                        // totalUsage 在遍历时直接累加，排除 isPeriod
                        if (!pointMeta?.isPeriod && !isNil(pointValue)) {
                          totalUsage += Number(pointValue);
                        }
                      });

                      // 当前系列总和
                      const subtotal = arr.filter(x => !isNil(x)).reduce((a, b) => Number(a) + Number(b), 0);

                      let color: string;

                      // 如果 series 自己有颜色
                      if (findSeries?.itemStyle?.color) {
                        color = findSeries.itemStyle.color as string;
                      } else {
                        const colors = Array.isArray(option!.color) ? option!.color : [option!.color];
                        color = colors[item.seriesIndex % colors.length] as string;
                      }

                      // 定义 rich 样式
                      const markerKey = `m${item.seriesIndex}`;
                      rich[markerKey] = {
                        height: 10,
                        width: 10,
                        backgroundColor: color,
                        borderRadius: 5,
                      };

                      // 拼接带 marker 的文本
                      if (!item.seriesName.includes('series')) {
                        lines.push(
                          `{${markerKey}|} ${item.seriesName}：${formatNumber(subtotal)} ${datas.generalUnitName}`
                        );
                      }
                    });

                    // 计算 totalCost：从"总用能成本"系列获取选中点的成本累加
                    const costSeries = series?.find(i => i.name === '总用能成本');
                    if (costSeries && Array.isArray(costSeries.data)) {
                      // 获取所有选中数据点的索引
                      const allSelectedIndices: number[] = [];
                      brushComponent.selected.forEach((item: any) => {
                        if (item?.dataIndex?.length) {
                          allSelectedIndices.push(...item.dataIndex);
                        }
                      });
                      // 去重并排序
                      const uniqueIndices = [...new Set(allSelectedIndices)].sort((a, b) => a - b);
                      uniqueIndices.forEach((idx: number) => {
                        const costPoint = costSeries.data[idx];
                        if (costPoint) {
                          const costValue = Array.isArray(costPoint)
                            ? costPoint[1]
                            : costPoint?.value?.[1] ?? costPoint?.value;
                          if (!isNil(costValue)) {
                            totalCost += Number(costValue);
                          }
                        }
                      });
                    }

                    if (brushComponent.areas.length > 0) {
                      const chartWidth = chartObject?.getWidth?.() || 0;
                      let left = brushComponent.areas[0].range[1] - 340; // 你原来的定位方式
                      // 防止 title 超出右边界
                      if (left + 340 > chartWidth) {
                        left = chartWidth - 340 - 10; // 240是title宽度，10是边距
                      }
                      if (left < 0) {
                        left = 0; // 防止超出左边界
                      }
                      chartObject?.setOption({
                        title: {
                          show: true,
                          padding: 8,
                          backgroundColor: theme === Themes.DARK ? 'rgba(0,0,0,0.8)' : '#fff',
                          borderColor: 'transparent',
                          borderRadius: 2,
                          textStyle: {
                            color: theme === Themes.DARK ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.85)',
                            fontSize: 12,
                            fontWeight: 500,
                            lineHeight: 20,
                            rich,
                          },
                          text: [
                            `${dayjs(Number(startTime)).format(xLabel)} ~ ${dayjs(Number(endTime)).format(xLabel)}`,
                            `总用量：${formatNumber(totalUsage)} ${datas.generalUnitName}`,
                            `总用能成本：${formatNumber(totalCost)} 元`,
                            ...lines,
                          ].join('\n'),
                          top: 100,
                          left,
                          zlevel: 99999,
                        },
                      });
                    } else {
                      chartObject?.setOption({
                        title: {
                          show: false,
                        },
                      });
                    }
                  },
                }}
              />
            </Spin>
          </ResizeObserver>
        </div>
      </div>
    </div>
  );
};

export default MakeUseOf;
